import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PackageSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/packages/purchase/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) { setTimeout(poll, 2000); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-24 text-center" data-testid="package-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
          <h1 className="font-display text-3xl font-semibold mt-6">Confirming your purchase…</h1>
          <p className="text-muted-foreground mt-2">Please wait, this only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold mt-4" data-testid="package-paid">Package purchased</h1>
          <p className="text-muted-foreground mt-2">You can now schedule each included service whenever you like.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/my-packages"><Button className="rounded-sm" data-testid="go-my-packages">Schedule my services</Button></Link>
            <Link to="/packages"><Button variant="outline" className="rounded-sm">Browse packages</Button></Link>
          </div>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="w-16 h-16 text-destructive mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold mt-4">{state === "timeout" ? "Still processing" : "Purchase not completed"}</h1>
          <p className="text-muted-foreground mt-2">{state === "timeout" ? "We couldn't confirm the payment yet. Check My Packages shortly." : "The payment wasn't completed. Please try again."}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/packages"><Button className="rounded-sm">Back to packages</Button></Link>
            <Link to="/my-packages"><Button variant="outline" className="rounded-sm">My packages</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}
